import React, { createContext, useState } from 'react';
import PropTypes from 'prop-types';

const COOKIE_CONSENT_COOKIE_NAME = 'cp_pdfe_cookie_consent';

function getCookie(name) {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');

  const cookie = cookies.find(c => c.trim().startsWith(nameEQ));
  if (!cookie) {
    return null;
  }

  const [, value] = cookie.split('=');
  return decodeURIComponent(value) || null;
}

function getConsentFromCookie() {
  return JSON.parse(getCookie(COOKIE_CONSENT_COOKIE_NAME));
}

function setCookie(name, value) {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/`;
}

function saveConsentAsACookie(consentObject) {
  window.consentObject = consentObject;
  setCookie(COOKIE_CONSENT_COOKIE_NAME, JSON.stringify(consentObject));
}

const GDPR_RULES = 'GDPR';
const CCPA_RULES = 'CCPA';
const NOT_REQUIRED = 'OTHER';
export const CONSENT_MODELS = {
  GDPR_RULES,
  CCPA_RULES,
  NOT_REQUIRED,
};

function getConsentModelFromQueryParams() {
  try {
    const cookieRulesFromQuery = new URLSearchParams(document.location.search).get('cookie_rules');

    if (cookieRulesFromQuery === 'gdpr') {
      return GDPR_RULES;
    }

    if (cookieRulesFromQuery === 'ccpa') {
      return CCPA_RULES;
    }
  } catch (_error) {
    return null;
  }

  return null;
}

function getConsentModelFromWindowObject() {
  return window.COOKIE_RULES_FROM_GEOLOCATION;
}

const ESSENTIAL = 'essential';
const FUNCTIONAL = 'functional';
const MARKETING_AND_ADVERTISING = 'marketingAndAdvertising';
export const COOKIE_CATEGORIES = {
  ESSENTIAL,
  FUNCTIONAL,
  MARKETING_AND_ADVERTISING,
};

function createConsentObject({ defaultValue }) {
  return {
    [ESSENTIAL]: true,
    [FUNCTIONAL]: Boolean(defaultValue),
    [MARKETING_AND_ADVERTISING]: Boolean(defaultValue),
  };
}

const CookiesContext = createContext({});

/**
 * A GPC Signal is essentially a "live revocation of consent to any non-essential cookie",
 * which means it takes precedence over any previously stored user preference,
 * AND that we shouldn't ask them for their consent whatsoever,
 * since they've already set their preference as "essential only" with the GPC Signal
 */
function ensureGPCSignalTakesPrecedence() {
  const shouldDisableCookiesFromGPCSignal = window.DISABLE_COOKIES_FROM_GPC_SIGNAL;

  if (shouldDisableCookiesFromGPCSignal) {
    saveConsentAsACookie(createConsentObject({ defaultValue: false }));
  }
}

export function CookiesContextProvider({ children }) {
  const consentModel = getConsentModelFromQueryParams() || getConsentModelFromWindowObject() || NOT_REQUIRED;
  const requiresUserConsent = [CCPA_RULES, GDPR_RULES].includes(consentModel);
  ensureGPCSignalTakesPrecedence();

  const usesNoSpecificRules = consentModel === CONSENT_MODELS.NOT_REQUIRED;
  const defaultConsentObject = createConsentObject({ defaultValue: usesNoSpecificRules });
  const consentObjectFromCookie = getConsentFromCookie();
  const [consentObject, setConsentObject] = useState(consentObjectFromCookie || defaultConsentObject);

  const shouldShowTheBanner = !consentObjectFromCookie;
  const [showBanner, setShowBanner] = useState(shouldShowTheBanner);
  const [showModal, setShowModal] = useState(false);
  const [showYourPrivacyChoicesModal, setShowYourPrivacyChoicesModal] = useState(false);

  function closeAllAndReload() {
    setShowBanner(false);
    setShowModal(false);
    setTimeout(() => window.location.reload(), 100);
  }

  function acceptAll() {
    const acceptanceConsentObject = createConsentObject({ defaultValue: true });
    setConsentObject(acceptanceConsentObject);
    saveConsentAsACookie(acceptanceConsentObject);
    closeAllAndReload();
  }

  function rejectAll() {
    const rejectionConsentObject = createConsentObject({ defaultValue: false });
    setConsentObject(rejectionConsentObject);
    saveConsentAsACookie(rejectionConsentObject);
    closeAllAndReload();
  }

  function openModal() {
    setShowBanner(false);
    setShowYourPrivacyChoicesModal(false);
    setShowModal(true);
  }

  function openYourPrivacyChoicesModal() {
    setShowYourPrivacyChoicesModal(true);
  }

  function closeYourPrivacyChoicesModal() {
    setShowYourPrivacyChoicesModal(false);
  }

  function saveCurrentSelection(selection = consentObject) {
    saveConsentAsACookie(selection);
    closeAllAndReload();
  }

  return (
    <CookiesContext.Provider
      value={{
        consentModel,
        requiresUserConsent,
        consentObject,
        showBanner,
        showModal,
        openModal,
        showYourPrivacyChoicesModal,
        openYourPrivacyChoicesModal,
        closeYourPrivacyChoicesModal,
        acceptAll,
        rejectAll,
        saveCurrentSelection,
      }}>
      {children}
    </CookiesContext.Provider>
  );
}
CookiesContextProvider.propTypes = {
  children: PropTypes.node,
};

export default CookiesContext;
